

// const BASE_URL = "https://staging.pteshstudios.com";

import { useState, useEffect, useRef } from "react";
import "./career.css";
import "./homepageEditor.css"; // adjust path as needed
import Avatar from '../../Images/image14.png';

const API_BASE      = "http://localhost:5000/api/careers";
const UPLOAD_URL    = "http://localhost:5000/api/upload";
const SERVER_ORIGIN = "http://localhost:5000";

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

const DEFAULT_DATA = {
  heroImage: "",
  title: "Careers",
  introText: "We are always on the lookout for potential candidates and difference makers that can really make their mark in Bell Oil & Gas. Please upload your CV in the following areas:",
  careerItems: [
    "Administration",
    "Business Development",
    "Construction and Engineering",
    "Corporate Affairs",
    "Drilling Services",
    "Finance",
    "Human Resources",
    "Offshore Maintenance, Production and Support Services",
    "Operations – Glass Fibre Reinforced Epoxy (GRE)",
    "Operations – Hose Assembly (Hydraulics)",
    "Valve Assembly and Repairs (Valve cleaning, assembly and disassembly technician, Valves machining and welding technician, Sand blasting and painting technician, Valve Testing Inspector, QC Officer)",
    "OCTG Threading and machining (CNC Machine operator, Equipment maintenance engineer (Electrical), Equipment maintenance (technician mechanical), hydraulic press operator, Technology Engineer, Crane Operator, General Machines Operator, QC Officer).",
    "Operations – Well Testing",
    "Project Management",
    "QHSE",
    "Rotating Equipment",
    "Supply Chain Management",
  ],
  closingText: "We will contact you when we have an opening in your area of interest. We appreciate your interest in our career opportunities.",
};

/* =========================================================
   IMAGE UPLOAD FIELD
========================================================= */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview,   setPreview]   = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
    } finally { setUploading(false); }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && <div className="editor-img-uploading"><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></div>}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* =========================================================
   EDIT MODAL
========================================================= */
function CareersEditModal({ data, onSave, onClose, saving }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  const setItem = (i, val) =>
    setDraft(d => { const careerItems = [...d.careerItems]; careerItems[i] = val; return { ...d, careerItems }; });

  const addItem = () =>
    setDraft(d => ({ ...d, careerItems: [...d.careerItems, "New career area"] }));

  const removeItem = (i) =>
    setDraft(d => ({ ...d, careerItems: d.careerItems.filter((_, idx) => idx !== i) }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit Careers Page</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* HERO IMAGE */}
          <p className="editor-section__title">Hero Image</p>
          <div className="editor-group">
            <ImageUploadField
              label="Page Banner Image"
              currentUrl={draft.heroImage}
              onUploaded={url => setDraft(d => ({ ...d, heroImage: url }))}
            />
          </div>

          {/* PAGE CONTENT */}
          <p className="editor-section__title">Page Content</p>
          <div className="editor-group">
            <label>Page Title</label>
            <input
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            />
            <label>Intro Text</label>
            <textarea
              rows={4}
              value={draft.introText}
              onChange={e => setDraft(d => ({ ...d, introText: e.target.value }))}
            />
            <label>Closing Text</label>
            <textarea
              rows={3}
              value={draft.closingText}
              onChange={e => setDraft(d => ({ ...d, closingText: e.target.value }))}
            />
          </div>

          {/* CAREER ITEMS */}
          <p className="editor-section__title">Career Areas</p>
          <div className="editor-group">
            <label>List Items</label>
            {draft.careerItems.map((item, i) => (
              <div key={i} className="editor-value-row" style={{ alignItems: "flex-start" }}>
                <textarea
                  rows={2}
                  value={item}
                  onChange={e => setItem(i, e.target.value)}
                  style={{ flex: 1 }}
                />
                <button className="editor-value-row__remove" onClick={() => removeItem(i)} title="Remove">✕</button>
              </div>
            ))}
            <button className="editor-add-btn" onClick={addItem}>+ Add Career Area</button>
          </div>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* =========================================================
   MAIN EXPORT
========================================================= */
const AdminCareer = () => {
  const [pageData,  setPageData]  = useState(DEFAULT_DATA);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res  = await fetch(API_BASE);
      const json = await res.json();
      if (json.success) setPageData(json.data);
    } catch (err) { console.error("Fetch error:", err); }
  };

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const res  = await fetch(API_BASE, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      await fetchData();
      setModalOpen(false);
      setSaveMsg("Changes saved successfully!");
    } catch (err) {
      setSaveMsg("Save failed. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3500);
    }
  };

  const heroBg = resolveUrl(pageData.heroImage) || Avatar;

  return (
    <>
      <button className="edit-fab" onClick={() => setModalOpen(true)} title="Edit page content">
        <span className="edit-fab__icon">✏️</span>
        <span className="edit-fab__label">Edit Page</span>
      </button>

      {saveMsg && <div className="editor-toast">{saveMsg}</div>}

      {modalOpen && (
        <CareersEditModal
          data={pageData}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      <div className="img_carrier">
        <img src={heroBg} alt="" />
      </div>

      <div className="careers-container">
        <div className="careers-content">
          <div className="title-section">
            <h1 className="careers-title">{pageData.title}</h1>
            <div className="orange-underline"></div>
          </div>

          <p className="intro-text">{pageData.introText}</p>

          <ul className="career-list">
            {pageData.careerItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <p className="closing-text">{pageData.closingText}</p>
        </div>
      </div>
    </>
  );
};

export default AdminCareer;